import React from "react";
import { Link as GatsbyLink } from "gatsby";

import { Image, Link } from "@global";
import { useFooterQuery } from "@query";
import {
  IPAD_PRO_BREAKPOINT,
  isAnathoth,
  isEveryday,
  TABLET_BREAKPOINT,
} from "@util/constants";
import { pageWidthMargin } from "@util/helper";
import { Container, P } from "@util/standard";
import styled from "styled-components";
import KlaviyoEmbed from "./klaviyoEmbed";
import NewsletterForm from "./newsletterForm";
import { determineBrand } from "@util/theme";

const TopContainer = styled(Container)`
  background-color: var(--theme-primary-color);
  .klaviyo-form {
    padding: 0 !important;
  }
`;

const Desktop = styled(Container)`
  background-color: var(--theme-primary-color);

  &.everyday {
    a {
      font-size: 16px;
    }
  }

  @media only screen and (max-width: ${IPAD_PRO_BREAKPOINT}px) {
    display: none;
  }
`;

const Mobile = styled(Container)`
  display: none;
  background-color: var(--theme-primary-color);

  @media only screen and (max-width: ${IPAD_PRO_BREAKPOINT}px) {
    display: flex;
    a {
      font-size: 14px;
    }
  }
`;

const NewsletterContainer = styled(Container)`
  column-gap: 30px;
  color: white;
  @media only screen and (max-width: ${TABLET_BREAKPOINT}px) {
    border-left: 0;
    max-width: 400px;
    margin: auto;
  }
`;

const KlaviyoEmbedSlot = styled.div`
  min-width: 500px;

  @media only screen and (max-width: ${TABLET_BREAKPOINT}px) {
    min-width: unset;
    max-width: 90vw;
  }
`;

const StyledH3 = styled.h3`
  &.barkers-nz,
  &.barkers-aus {
    font-size: 30px;

    @media only screen and (max-width: ${TABLET_BREAKPOINT}px) {
      font-size: 25px;
    }
  }

  &.anathoth {
    font-family: var(--theme-font);
    letter-spacing: 0.01em;
  }
`;

const Footer = () => {
  const footerData = useFooterQuery();
  if (!footerData) return null;
  const { footerContent } = footerData;
  const content =
    footerContent?.[0] as Queries.Maybe<Queries.SanityFooterItems>;
  if (!content) return null;

  const { logo } = content;
  const margin = pageWidthMargin();
  const brand = determineBrand();

  console.log(process.env.GATSBY_KLAVIYO_SUBSCRIBE_FORM_CLASS);

  return (
    <Container
      display="flex"
      flexDirection="column"
      width="100%"
      margin="auto"
      height="auto"
    >
      <TopContainer width="100%">
        <Container
          width={`calc(100% - ${margin} - ${margin})`}
          margin="auto"
          padding="100px 0"
          mobilePadding="55px 0 66px"
          flexWrap="wrap"
          alignItems="flex-start"
        >
          <NewsletterContainer
            width="100%"
            tabletWidth="100%"
            flexDirection="row"
            mobilePadding="35px 0 0"
            isMobileColumn
          >
            {content.newsletter?.title && (
              <Container
                flexDirection="column"
                tabletMargin="0 0 25px"
                width="40%"
                tabletWidth="100%"
                mobileWidth="100%"
              >
                <StyledH3 className={brand}>
                  {content.newsletter?.title}
                </StyledH3>
                {content.newsletter?.description && (
                  <P margin="0">{content.newsletter?.description}</P>
                )}
              </Container>
            )}
            {Boolean(process.env.GATSBY_MAILCHIMP_ENDPOINT) ? (
              <NewsletterForm />
            ) : (
              <KlaviyoEmbedSlot>
                <KlaviyoEmbed
                  className={
                    process.env.GATSBY_KLAVIYO_SUBSCRIBE_FORM_CLASS ??
                    "klaviyo-form-QPP5AY"
                  }
                />
              </KlaviyoEmbedSlot>
            )}
          </NewsletterContainer>
        </Container>
      </TopContainer>

      <Desktop
        margin="auto"
        width="100%"
        className={isEveryday ? "everyday" : undefined}
      >
        <Container width={`calc(100% - ${margin} - ${margin})`} margin="auto">
          <Container
            height="140px"
            width="100%"
            display="flex"
            alignItems="center"
            flexDirection="row"
            justifyContent="space-between"
          >
            <GatsbyLink className="imageLink" to="/">
              <Image
                staticImage={logo && logo.asset?.url}
                altText={(logo?.altText as string) ?? ""}
                width={isAnathoth ? "150px" : "100px"}
                height="auto"
              />
            </GatsbyLink>
            {content.links && (
              <Container
                display="flex"
                alignItems="center"
                flexDirection="row"
                flexWrap="wrap"
                justifyContent="center"
              >
                {content.links.map(link => {
                  if (!link) return;
                  return (
                    <Container key={link._key} margin="auto 10px">
                      <Link color="white" {...link} />
                    </Container>
                  );
                })}
              </Container>
            )}

            {content.socialLinks && (
              <Container display="flex" alignItems="center">
                {content.socialLinks?.map(socialLink => {
                  if (!socialLink) return;
                  return (
                    <Container key={socialLink._key} margin="auto 10px">
                      <Link
                        newwindow
                        url={socialLink.linkTo}
                        asset={socialLink?.icon?.asset?.gatsbyImageData}
                      />
                    </Container>
                  );
                })}
              </Container>
            )}
          </Container>
        </Container>
      </Desktop>

      <Mobile>
        <Container margin="20px auto" width="100%">
          <Container
            width="80%"
            margin="auto"
            display="flex"
            justifyContent="space-between"
          >
            <Container
              height="auto"
              display="flex"
              flexDirection="column"
              justifyContent="space-between"
              alignItems="center"
            >
              <Image
                staticImage={logo?.asset?.url}
                altText={(logo?.altText as string) ?? ""}
                width="100px"
                height="auto"
              />

              {content.socialLinks && (
                <Container display="flex" alignItems="center">
                  {content.socialLinks.map(socialLink => {
                    if (!socialLink) return;
                    return (
                      <Container key={socialLink._key} margin="auto 5px">
                        <Link
                          newwindow
                          url={socialLink.linkTo}
                          asset={socialLink?.icon?.asset?.gatsbyImageData}
                        />
                      </Container>
                    );
                  })}
                </Container>
              )}
            </Container>
            {content.links && (
              <Container
                display="flex"
                alignItems="left"
                margin="auto 40px"
                flexDirection="column"
              >
                {content.links.map(link => {
                  if (!link) return;
                  return (
                    <Container key={link._key} margin="4px 0px">
                      <Link color="white" {...link} />
                    </Container>
                  );
                })}
              </Container>
            )}
          </Container>
        </Container>
      </Mobile>
    </Container>
  );
};

export default Footer;
